import {CANVAS_BACKDROP_CLASS, PAGE_CONTAINER_CLASS} from '@PosterWhiteboard/page/page.types';

/**
 * A page's hashedID is generated in the browser and travels through the saved poster, so a poster
 * can arrive carrying whatever its author put there. It reaches the DOM in two directions, and both
 * are built here so neither can be reached with a raw value.
 *
 * Writing it into an element, it is set as a property — never interpolated into markup, where it
 * could close the attribute it sits in and open an element of its own.
 *
 * Reading it back, the page is found by comparing the property. Putting the value into a selector
 * string would need it escaped for that position, and an unescaped quote makes `querySelector`
 * throw on a selector it can no longer parse. Comparing the property needs no escaping at all.
 */
export const createPageContainerElement = (pageId: string): HTMLDivElement => {
  const pageContainer = document.createElement('div');
  pageContainer.className = `${PAGE_CONTAINER_CLASS} js-canvas`;
  pageContainer.dataset.id = pageId;

  return pageContainer;
};

export const createCanvasBackdropElement = (pageId: string, width: number, height: number): HTMLDivElement => {
  const canvasBackdrop = document.createElement('div');
  canvasBackdrop.id = pageId;
  canvasBackdrop.className = `${CANVAS_BACKDROP_CLASS} _position-relative`;
  canvasBackdrop.style.width = `${width}px`;
  canvasBackdrop.style.height = `${height}px`;

  return canvasBackdrop;
};

export const findPageContainerElement = (pageId: string): HTMLDivElement | undefined => {
  for (const pageContainer of document.querySelectorAll<HTMLDivElement>(`.${PAGE_CONTAINER_CLASS}`)) {
    if (pageContainer.dataset.id === pageId) {
      return pageContainer;
    }
  }

  return undefined;
};

export const findInPageContainer = (pageId: string, childSelector: string): HTMLElement | undefined => {
  return findPageContainerElement(pageId)?.querySelector<HTMLElement>(childSelector) ?? undefined;
};

/**
 * How far below the first page's backdrop the given page's backdrop sits, in CSS pixels. Item
 * popups are positioned in a layer that starts at the first page, so a popup for another page adds
 * this. Measured from the backdrops (which move together with the page scroll), never from the
 * canvas elements: those are offset within their own page's backdrop and are shifted inside it as
 * the page scrolls, so their offsetTops are not comparable across pages.
 */
export const getPageBackdropOffsetFromFirstPage = (pageId: string): number => {
  const firstPageBackdrop = document.querySelector<HTMLElement>(`.${PAGE_CONTAINER_CLASS} .${CANVAS_BACKDROP_CLASS}`);
  const pageBackdrop = findPageCanvasBackdropElement(pageId);
  if (!firstPageBackdrop || !pageBackdrop) {
    return 0;
  }
  return pageBackdrop.getBoundingClientRect().top - firstPageBackdrop.getBoundingClientRect().top;
};

/** How far below the given element's top the page's backdrop sits, in CSS pixels; 0 when either is missing. */
export const getPageBackdropOffsetFromElement = (pageId: string, referenceElement: HTMLElement): number => {
  const pageBackdrop = findPageCanvasBackdropElement(pageId);
  if (!pageBackdrop) {
    return 0;
  }
  return pageBackdrop.getBoundingClientRect().top - referenceElement.getBoundingClientRect().top;
};

export const findPageCanvasBackdropElement = (pageId: string): HTMLDivElement | undefined => {
  const canvasBackdrop = findInPageContainer(pageId, `.${CANVAS_BACKDROP_CLASS}`);

  return canvasBackdrop instanceof HTMLDivElement ? canvasBackdrop : undefined;
};
